import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dna, Sparkles, Users } from "lucide-react";

export default function Networks() {
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [nets, setNets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const fetchNets = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/intel/networks"); setNets(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNets(); }, [fetchNets]);

  const analyze = async (name) => {
    setBusy(name);
    try {
      await api.post("/intel/network/analyze", null, { params: { name } });
      toast.success("Network analysed");
      fetchNets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="networks-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-purple-700 flex items-center gap-1.5"><Dna className="h-4 w-4" /> Analytical intelligence</div>
        <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Networks</h1>
        <p className="text-sm text-slate-500 mt-1">AI-assisted behaviour, roles, operational workflow and Network DNA — investigative leads for human review.</p>
      </header>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="networks-loading">Loading networks…</div>
      ) : nets.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500">No named networks yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {nets.map((n) => (
            <div key={n.network} data-testid={`network-card-${n.network}`} className="border border-slate-200 bg-white rounded-md p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/intel/networks/${encodeURIComponent(n.network)}`} className="font-display font-bold tracking-tight text-base hover:underline">{n.network}</Link>
                {n.analyzed && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200 text-teal-700">analysed</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 font-mono">
                <span>{n.entity_count} entities</span>
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{n.actor_count} actors</span>
              </div>
              {n.summary && <p className="text-sm text-slate-600 line-clamp-3">{n.summary}</p>}
              <div className="flex gap-2">
                <Link to={`/intel/networks/${encodeURIComponent(n.network)}`} data-testid={`view-network-${n.network}`}
                  className="text-xs border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-md">View brief</Link>
                {!readOnly && (
                  <Button data-testid={`analyze-network-${n.network}`} size="sm" disabled={busy === n.network}
                    onClick={() => analyze(n.network)} className="bg-purple-600 hover:bg-purple-700 gap-1.5 h-8">
                    <Sparkles className="h-3.5 w-3.5" /> {busy === n.network ? "Analysing…" : n.analyzed ? "Re-analyse" : "Analyse"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
